/**
 * Supplier Evaluation backend API wrapper.
 * Base URL from NEXT_PUBLIC_SUPPLIER_EVAL_API env var.
 * Matches VITE_SUPPLIER_EVAL_API from React source exactly.
 */

const BASE = process.env.NEXT_PUBLIC_SUPPLIER_EVAL_API ?? "http://localhost:8082";

async function getBearerToken(): Promise<string | null> {
  try {
    const { getAuth } = await import("firebase/auth");
    const user = getAuth().currentUser;
    return user ? user.getIdToken() : null;
  } catch {
    return null;
  }
}

function authHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Form generation ───────────────────────────────────────────────────────────

export async function generateAssessmentForm(params: {
  industry_vertical: string;
  evaluation_focus: string[];
  additional_context?: string;
}): Promise<{ form_json: Record<string, unknown> }> {
  const token = await getBearerToken();
  const body = new URLSearchParams({
    industry_vertical: params.industry_vertical,
    evaluation_focus: JSON.stringify(params.evaluation_focus),
    design_responsible: "true",
    commodity_category: "",
    additional_context: params.additional_context ?? "",
  });
  const res = await fetch(`${BASE}/api/agents/form-generator/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...authHeaders(token),
    },
    body,
  });
  if (!res.ok) throw new Error(res.statusText);
  const result = await res.json();
  return result.data?.form_json ?? result.data;
}

export async function suggestEvaluationFocus(industry: string): Promise<string[]> {
  const token = await getBearerToken();
  const body = new FormData();
  body.append("industry", industry);
  const res = await fetch(`${BASE}/api/agents/evaluation-focus/suggest`, {
    method: "POST",
    headers: authHeaders(token),
    body,
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  return json.data.categories as string[];
}

// ── Email notifications ───────────────────────────────────────────────────────

export async function sendSupplierInvitation(params: {
  supplier_email: string;
  contact_person: string;
  company_name: string;
  evaluation_focus: string[];
  industry: string;
  portal_link: string;
  additional_context?: string;
}): Promise<void> {
  const token = await getBearerToken();
  const body = new URLSearchParams({
    supplier_email: params.supplier_email,
    contact_person: params.contact_person,
    company_name: params.company_name,
    evaluation_focus: JSON.stringify(params.evaluation_focus),
    industry: params.industry,
    portal_link: params.portal_link,
    ...(params.additional_context ? { additional_context: params.additional_context } : {}),
  });
  const res = await fetch(`${BASE}/api/email/send-supplier-invitation`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...authHeaders(token),
    },
    body,
  });
  if (!res.ok) throw new Error(`Email failed: ${res.statusText}`);
}

export async function sendSupplierReminder(params: {
  supplier_email: string;
  contact_person: string;
  company_name: string;
  evaluation_focus: string[];
  industry: string;
  portal_link: string;
  additional_context?: string;
}): Promise<void> {
  const token = await getBearerToken();
  const body = new URLSearchParams({
    supplier_email: params.supplier_email,
    contact_person: params.contact_person,
    company_name: params.company_name,
    evaluation_focus: JSON.stringify(params.evaluation_focus),
    industry: params.industry,
    portal_link: params.portal_link,
    ...(params.additional_context ? { additional_context: params.additional_context } : {}),
  });
  const res = await fetch(`${BASE}/api/email/send-supplier-reminder`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...authHeaders(token),
    },
    body,
  });
  if (!res.ok) throw new Error(`Reminder failed: ${res.statusText}`);
}

export async function sendCollaboratorInvite(params: {
  collaborator_email: string;
  collaborator_name: string;
  company_name: string;
  project_name: string;
  portal_link: string;
}): Promise<void> {
  const token = await getBearerToken();
  const body = new URLSearchParams(params);
  const res = await fetch(`${BASE}/api/email/send-collaborator-invite`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...authHeaders(token),
    },
    body,
  });
  if (!res.ok) throw new Error("Email failed");
}

// ── File upload/download ──────────────────────────────────────────────────────

export async function generateUploadUrl(params: {
  evaluation_id: string;
  question_id: string;
  filename: string;
  content_type: string;
  upload_token: string;
  label?: string;
}): Promise<{ upload_url: string; gcs_key: string; file_id: string }> {
  const res = await fetch(`${BASE}/api/files/generate-upload-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...params, label: params.label ?? "Evidence" }),
  });
  if (!res.ok) throw new Error("Failed to get upload URL");
  return res.json();
}

export async function generateDownloadUrl(params: {
  evaluation_id: string;
  question_id: string;
  gcs_key: string;
  upload_token?: string;
}): Promise<string> {
  const token = await getBearerToken();
  const res = await fetch(`${BASE}/api/files/generate-download-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(token),
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error("Failed to get download URL");
  const { download_url } = await res.json();
  return download_url as string;
}

export { BASE as SUPPLIER_EVAL_API_BASE };

// ── Analysis generation ───────────────────────────────────────────────────────

export async function generateAnalysis(responseDoc: unknown): Promise<Record<string, unknown>> {
  const token = await getBearerToken();
  const body = new FormData();
  body.append("completed_form_json", JSON.stringify(responseDoc));
  const res = await fetch(`${BASE}/api/agents/analysis/generate`, {
    method: "POST",
    headers: authHeaders(token),
    body,
  });
  if (!res.ok) throw new Error(`Analysis API error: ${res.statusText}`);
  const result = await res.json();
  if (result.status !== "success" || !result.data) {
    throw new Error(result.message ?? "Analysis generation failed");
  }
  return result;
}
